import { useEffect, useState, useCallback } from "react";
import { Loader2, XCircle, Users, Eye, BarChart3, Gift, CreditCard, Lock, Unlock } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const fmtDate = (s) => (s ? new Date(s).toLocaleDateString("pt-BR") : "—");
const eur = (n) => `€${Number(n || 0).toLocaleString("pt-PT")}`;

function StatCard({ label, value, icon: Icon, tone = "green" }) {
  const tones = { green: "text-[#1b5e3f] bg-[#e8f0ec]", amber: "text-[#d97706] bg-[#fef3c7]", blue: "text-[#2563eb] bg-[#dbeafe]" };
  return (
    <div className="bg-white rounded-2xl border border-[#e7e5e4] p-4 flex items-center gap-3">
      <span className={`w-10 h-10 rounded-xl flex items-center justify-center ${tones[tone]}`}><Icon className="w-5 h-5" /></span>
      <div>
        <p className="text-xs text-[#78716c]">{label}</p>
        <p className="font-head text-xl font-bold text-[#1c1917]">{value}</p>
      </div>
    </div>
  );
}

export function ControlPanel() {
  const [loading, setLoading] = useState(true);
  const [paid, setPaid] = useState({ rows: [], totals: {} });
  const [filter, setFilter] = useState("all");
  const [logs, setLogs] = useState({ logs: [], total: 0 });
  const [site, setSite] = useState({ total: 0, by_day: [], by_path: [] });
  const [views, setViews] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, l, s, v] = await Promise.all([
        api.get(`/admin/control/paid-users`, { params: filter !== "all" ? { source: filter } : {} }),
        api.get(`/admin/control/access-logs`),
        api.get(`/admin/control/site-stats`),
        api.get(`/admin/control/listing-views`),
      ]);
      setPaid(p.data); setLogs(l.data); setSite(s.data); setViews(v.data);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const blockUser = async (row, blocked) => {
    const verb = blocked ? "BLOQUEAR o acesso" : "DESBLOQUEAR o acesso";
    if (!window.confirm(`Confirma ${verb} de ${row.name}?`)) return;
    try {
      await api.post(`/admin/users/${row.user_id}/block`, { blocked });
      toast.success(blocked ? "Acesso bloqueado." : "Acesso desbloqueado.");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const revoke = async (row) => {
    const url = row.field === "advertiser" ? `/admin/users/${row.user_id}/revoke-advertiser` : `/admin/users/${row.user_id}/revoke-subscription`;
    if (!window.confirm(`Confirma revogar ${row.kind} de ${row.name}? O acesso ao benefício será removido imediatamente.`)) return;
    try {
      await api.post(url);
      toast.success(`${row.kind} revogada.`);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  if (loading && !views) return <div className="py-16 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#1b5e3f]" /></div>;

  const t = paid.totals || {};

  return (
    <div className="space-y-8" data-testid="control-panel">
      {/* Assinantes ativos / pagamentos */}
      <div className="grid sm:grid-cols-5 gap-3">
        <StatCard label="Cadastros ativos" value={t.count ?? 0} icon={Users} />
        <StatCard label="Pagos (Stripe)" value={t.stripe ?? 0} icon={CreditCard} tone="blue" />
        <StatCard label="Voucher grátis" value={t.voucher ?? 0} icon={Gift} tone="amber" />
        <StatCard label="Cortesia 1 mês" value={t.cortesia ?? 0} icon={Gift} tone="green" />
        <StatCard label="Receita estimada" value={eur(t.revenue)} icon={BarChart3} />
      </div>

      <div className="bg-white rounded-2xl border border-[#e7e5e4] shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-[#e7e5e4] flex items-center justify-between gap-3 flex-wrap">
          <span className="font-head font-semibold text-[#1c1917] flex items-center gap-2"><Users className="w-4 h-4 text-[#1b5e3f]" /> Usuários ativos que já fizeram o pagamento</span>
          <select data-testid="control-source-filter" value={filter} onChange={(e) => setFilter(e.target.value)} className="bg-white border border-[#e7e5e4] rounded-xl px-3 py-1.5 text-sm">
            <option value="all">Todos</option>
            <option value="stripe">Pagos (Stripe)</option>
            <option value="voucher">Voucher grátis</option>
            <option value="cortesia">Cortesia 1 mês</option>
          </select>
        </div>
        {paid.rows.length === 0 ? (
          <div className="px-6 py-8 text-sm text-[#78716c]">Nenhum cadastro ativo neste filtro.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-[#78716c] border-b border-[#e7e5e4]">
                  <th className="px-6 py-3">Usuário</th>
                  <th className="px-4 py-3">Tipo</th>
                  <th className="px-4 py-3">Origem</th>
                  <th className="px-4 py-3">Valor</th>
                  <th className="px-4 py-3">Expira</th>
                  <th className="px-4 py-3">Ações</th>
                </tr>
              </thead>
              <tbody>
                {paid.rows.map((r, i) => (
                  <tr key={`${r.user_id}-${r.field}`} data-testid={`control-paid-row-${i}`} className="border-b border-[#f5f5f4]">
                    <td className="px-6 py-3">
                      <div className="font-medium text-[#1c1917] flex items-center gap-1">{r.name} {r.blocked && <span className="text-[10px] bg-red-100 text-red-600 px-1.5 py-0.5 rounded-full">Bloqueado</span>}</div>
                      <div className="text-xs text-[#78716c]">{r.email}</div>
                    </td>
                    <td className="px-4 py-3">{r.kind}</td>
                    <td className="px-4 py-3">
                      {r.source === "voucher"
                        ? <span className="text-xs bg-[#fef3c7] text-[#d97706] px-2 py-0.5 rounded-full inline-flex items-center gap-1"><Gift className="w-3 h-3" /> Voucher</span>
                        : r.source === "cortesia"
                        ? <span className="text-xs bg-[#e8f0ec] text-[#1b5e3f] px-2 py-0.5 rounded-full inline-flex items-center gap-1"><Gift className="w-3 h-3" /> Cortesia</span>
                        : <span className="text-xs bg-[#dbeafe] text-[#2563eb] px-2 py-0.5 rounded-full inline-flex items-center gap-1"><CreditCard className="w-3 h-3" /> Stripe</span>}
                    </td>
                    <td className="px-4 py-3 font-semibold text-[#1c1917]">{r.source === "stripe" ? eur(r.amount) : "Grátis"}</td>
                    <td className="px-4 py-3 text-[#78716c]">{fmtDate(r.expires_at)}</td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-2">
                        <button data-testid={`control-revoke-${i}`} onClick={() => revoke(r)} className="text-xs px-2.5 py-1 rounded-lg border border-[#d97706] text-[#d97706] hover:bg-[#fef3c7] inline-flex items-center gap-1"><XCircle className="w-3.5 h-3.5" /> Revogar</button>
                        {r.blocked ? (
                          <button data-testid={`control-unblock-${i}`} onClick={() => blockUser(r, false)} className="text-xs px-2.5 py-1 rounded-lg border border-[#1b5e3f] text-[#1b5e3f] hover:bg-[#e8f0ec] inline-flex items-center gap-1"><Unlock className="w-3.5 h-3.5" /> Desbloquear</button>
                        ) : (
                          <button data-testid={`control-block-${i}`} onClick={() => blockUser(r, true)} className="text-xs px-2.5 py-1 rounded-lg border border-[#ef4444] text-[#ef4444] hover:bg-red-50 inline-flex items-center gap-1"><Lock className="w-3.5 h-3.5" /> Bloquear</button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Consultas ao site */}
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl border border-[#e7e5e4] shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-[#e7e5e4] font-head font-semibold flex items-center gap-2"><BarChart3 className="w-4 h-4 text-[#1b5e3f]" /> Consultas ao site — {site.total} visitas</div>
          <div className="p-5 grid sm:grid-cols-2 gap-5">
            <div>
              <p className="text-xs uppercase tracking-wide text-[#78716c] mb-2">Visitas por dia (últimos)</p>
              <div className="space-y-1.5">
                {site.by_day.length === 0 ? <p className="text-sm text-[#78716c]">Sem dados ainda.</p> :
                  site.by_day.slice(0, 10).map((d) => (
                    <div key={d.day} className="flex items-center justify-between text-sm">
                      <span className="text-[#78716c]">{d.day}</span>
                      <span className="font-semibold text-[#1c1917]">{d.count}</span>
                    </div>
                  ))}
              </div>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide text-[#78716c] mb-2">Páginas mais acessadas</p>
              <div className="space-y-1.5">
                {site.by_path.length === 0 ? <p className="text-sm text-[#78716c]">Sem dados ainda.</p> :
                  site.by_path.slice(0, 10).map((p) => (
                    <div key={p.path} className="flex items-center justify-between text-sm gap-2">
                      <span className="text-[#78716c] truncate">{p.path}</span>
                      <span className="font-semibold text-[#1c1917] shrink-0">{p.count}</span>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-[#e7e5e4] shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-[#e7e5e4] font-head font-semibold flex items-center gap-2"><Eye className="w-4 h-4 text-[#1b5e3f]" /> Visualizações de anúncios</div>
          <div className="p-5 space-y-4">
            {views && [["Perfis", views.professionals], ["Imóveis", views.rentals], ["Vagas", views.jobs]].map(([label, data]) => (
              <div key={label}>
                <div className="flex items-center justify-between mb-1.5">
                  <p className="text-xs uppercase tracking-wide text-[#78716c]">{label}</p>
                  <span className="text-xs font-semibold text-[#1b5e3f]">{data.total} views totais</span>
                </div>
                {data.top.length === 0 ? <p className="text-sm text-[#78716c]">Sem visualizações ainda.</p> :
                  <div className="space-y-1">
                    {data.top.slice(0, 5).map((it) => (
                      <div key={it.id} className="flex items-center justify-between text-sm gap-2">
                        <span className="text-[#1c1917] truncate">{it.title || "—"}</span>
                        <span className="text-[#78716c] shrink-0 inline-flex items-center gap-1"><Eye className="w-3 h-3" /> {it.views}</span>
                      </div>
                    ))}
                  </div>}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Registro de acessos */}
      <div className="bg-white rounded-2xl border border-[#e7e5e4] shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-[#e7e5e4] font-head font-semibold flex items-center gap-2"><Lock className="w-4 h-4 text-[#1b5e3f]" /> Registro de acessos / logins ({logs.total})</div>
        {logs.logs.length === 0 ? (
          <div className="px-6 py-8 text-sm text-[#78716c]">Nenhum acesso registrado ainda.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-[#78716c] border-b border-[#e7e5e4]">
                  <th className="px-6 py-3">Usuário</th>
                  <th className="px-4 py-3">Tipo</th>
                  <th className="px-4 py-3">IP</th>
                  <th className="px-4 py-3">Data/Hora</th>
                </tr>
              </thead>
              <tbody>
                {logs.logs.map((l) => (
                  <tr key={l.id} className="border-b border-[#f5f5f4]">
                    <td className="px-6 py-3"><span className="font-medium text-[#1c1917]">{l.name}</span> <span className="text-xs text-[#78716c]">{l.email}</span></td>
                    <td className="px-4 py-3">{l.kind === "register" ? "Cadastro" : "Login"}</td>
                    <td className="px-4 py-3 text-[#78716c]">{l.ip || "—"}</td>
                    <td className="px-4 py-3 text-[#78716c]">{l.at ? new Date(l.at).toLocaleString("pt-BR") : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
